import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationService } from '../../../../../common/services';
import {
  LIST_SERVER_PAGINATION_INITIAL_STATE,
  ListServerPaginationStateModel,
  UnitSystemListModel,
} from '../../../../../common/models';
import { ShipmentWaybillGoodsItemsService } from './shipment-waybill-goods-items.service';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MeasurementUnitCodesEnum } from '../../../../../common/models/unit-system.model';
import {
  ShipmentWaybillGoodsItemsInitialState,
  ShipmentWaybillGoodsItemsLoad,
} from './shipment-waybill-goods-items.actions';
import { ShipmentWaybillGoodsItemModel } from '../../models/shipment-waybill-goods-items.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type ShipmentWaybillGoodsItemsStateModel = ListServerPaginationStateModel<ShipmentWaybillGoodsItemModel> &
  UnitSystemListModel;

const initialState: ShipmentWaybillGoodsItemsStateModel = {
  ...LIST_SERVER_PAGINATION_INITIAL_STATE,
  dimensionsUnitCode: null,
  weightUnitCode: null,
  volumeUnitCode: null,
};

@State<ShipmentWaybillGoodsItemsStateModel>({
  name: 'shipmentWaybillGoodsItems',
  defaults: initialState,
})
@Injectable()
export class ShipmentWaybillGoodsItemsState {
  constructor(
    private shipmentWaybillGoodsItemsService: ShipmentWaybillGoodsItemsService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ShipmentWaybillGoodsItemsStateModel): ShipmentWaybillGoodsItemsStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentWaybillGoodsItemsStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getGoodsItems(state: ShipmentWaybillGoodsItemsStateModel): ShipmentWaybillGoodsItemModel[] {
    return state.items;
  }

  @Selector()
  static getDimensionsUnitCode(state: ShipmentWaybillGoodsItemsStateModel): MeasurementUnitCodesEnum {
    return state.dimensionsUnitCode;
  }

  @Selector()
  static getWeightUnitCode(state: ShipmentWaybillGoodsItemsStateModel): MeasurementUnitCodesEnum {
    return state.weightUnitCode;
  }

  @Selector()
  static getVolumeUnitCode(state: ShipmentWaybillGoodsItemsStateModel): MeasurementUnitCodesEnum {
    return state.volumeUnitCode;
  }

  @Action(ShipmentWaybillGoodsItemsLoad)
  getWaybillGoodsItems(
    ctx: StateContext<ShipmentWaybillGoodsItemsStateModel>,
    { payload }: ShipmentWaybillGoodsItemsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentWaybillGoodsItemsService.getShipmentWaybillGoodsItems(payload).pipe(
      map((state: ShipmentWaybillGoodsItemsStateModel) => {
        ctx.patchState({ ...state, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentWaybillGoodsItemsInitialState)
  setInitialState(ctx: StateContext<ShipmentWaybillGoodsItemsStateModel>): void {
    ctx.setState(initialState);
  }
}
